import {
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Input,
	InputGroup,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
} from "@chakra-ui/react";
import { UseFormRegister, UseFormSetValue } from "react-hook-form";
import CertificateIcon from "../assets/certificate-icon.png";
import { Select as CRSelect } from "chakra-react-select";
import { Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import countries from "../assets/countries.json";
import currencies from "../assets/currencies.json";
import { AddressSelector } from "./AddressSelector";
import { EmploymentStatus } from "src/__generated__/graphql";

const EMPLOYMENT_STATUS_OPTIONS = [
	{ value: EmploymentStatus.Employed, label: "Employed" },
	{ value: EmploymentStatus.SelfEmployed, label: "Self Employed" },
	{ value: EmploymentStatus.Unemployed, label: "Unemployed" },
];

type ContactAndProfessionalInfo = {
	country: {
		value: string;
		label: string;
	};
	phoneNumber: string;
	detailedAddress: {
		placeId: string;
		address: string;
		formattedAddress: string;
		state?: string;
		city?: string;
	};
	employmentStatus: EmploymentStatus;
	incomeCurrency: {
		value: string;
		label: string;
	};
	averageMonthlyIncome?: number;
	yearsOfExperience?: number;
	noExperience: boolean;
};

type ContactAndProfessionalInformationFormProps = {
	registerContactAndProfessionalInfo: UseFormRegister<ContactAndProfessionalInfo>;
	setValue: UseFormSetValue<ContactAndProfessionalInfo>;
	touchedFields: Partial<
		Readonly<{
			detailedAddress?:
				| {
						address?: boolean | undefined;
						formattedAddress?: boolean | undefined;
						placeId?: boolean | undefined;
						state?: boolean | undefined;
						city?: boolean | undefined;
				  }
				| undefined;
			country?:
				| {
						value?: boolean | undefined;
						label?: boolean | undefined;
				  }
				| undefined;
			employmentStatus?: boolean | undefined;
			yearsOfExperience?: boolean | undefined;
			noExperience?: boolean | undefined;
			phoneNumber?: boolean | undefined;
			incomeCurrency?:
				| {
						value?: boolean | undefined;
						label?: boolean | undefined;
				  }
				| undefined;
			averageMonthlyIncome?: boolean | undefined;
		}>
	>;
	control: any;
	formData: ContactAndProfessionalInfo;
};

export default function ContactAndProfessionalInformationForm({
	registerContactAndProfessionalInfo,
	setValue,
	touchedFields,
	formData,
	control,
}: ContactAndProfessionalInformationFormProps) {
	const [userHasChangedCurrency, setUserHasChangedCurrency] =
		useState<boolean>(false);

	useEffect(() => {
		if (formData.country) {
			const selectedCountry =
				countries[formData.country.value as keyof typeof countries];
			const selectedCountryCurrency =
				currencies[selectedCountry.currency_code as keyof typeof currencies];

			if (selectedCountry && !userHasChangedCurrency) {
				setValue("incomeCurrency", {
					value: selectedCountryCurrency.code,
					label: selectedCountryCurrency.name,
				});
			}
		}
	}, [formData.country, setValue, userHasChangedCurrency]);

	const customStyles = {
		control: (baseStyles: Record<string, unknown>, state: any) => ({
			...baseStyles,
			height: {
				base: "40px",
				lg: "70px",
			},
			borderRadius: 0,
		}),
	};

	return (
		<div className="overflow-y-scroll grid grid-cols-1 lg:grid-cols-2">
			<div className="hidden lg:block py-8">
				<div className="mb-8 bg-[#EDEDED] w-[72px] h-[72px] rounded-full flex items-center justify-center p-6">
					<img src={CertificateIcon} alt="Certificate Icon" />
				</div>
				<div className="flex flex-col w-[70%]">
					<h2 className="mb-2 text-xl lg:text-2xl font-medium text-[#121212] font-[AeonikPro]">
						Contact and Professional Information
					</h2>
					<p className="text-lg lg:text-xl text-[#464646]">
						Provide your contact and professional details in the required
						fields.
					</p>
				</div>
			</div>
			<div className="border-l-[0] lg:border-l-[1px] border-[#E7E7E7] lg:pl-12 py-8 flex flex-col gap-6">
				<FormControl isInvalid={!!(touchedFields.country && !formData.country)}>
					<div className="flex flex-col gap-4">
						<label
							htmlFor="country"
							className="text-base lg:text-xl text-[#212121] font-medium"
						>
							Country
						</label>
						<div className="flex flex-col gap-2">
							<Controller
								name="country"
								control={control}
								defaultValue=""
								render={({ field }) => (
									<CRSelect
										id="country"
										placeholder="Select option"
										options={Object.values(countries).map((country) => ({
											label: country.name,
											value: country.alpha2,
										}))}
										size={{
											base: "md",
											lg: "lg",
										}}
										chakraStyles={customStyles}
										{...field}
									/>
								)}
							/>
							<FormErrorMessage>Please select your country</FormErrorMessage>
						</div>
					</div>
				</FormControl>

				<Controller
					name="detailedAddress"
					control={control}
					render={({ field }) => (
						<AddressSelector
							value={field.value}
							onChange={field.onChange}
							country={formData.country}
							onCountryChange={(co) => setValue("country", co)}
							isInvalid={
								!!(touchedFields.detailedAddress && !formData.detailedAddress)
							}
						/>
					)}
				/>

				<FormControl
					isInvalid={
						!!(touchedFields.employmentStatus && !formData.employmentStatus)
					}
				>
					<div className="flex flex-col gap-4">
						<label
							htmlFor="employmentStatus"
							className="text-base lg:text-xl text-[#212121] font-medium"
						>
							What is your employment status?
						</label>
						<div className="flex flex-col gap-2">
							<Controller
								name="employmentStatus"
								control={control}
								render={({ field }) => (
									<CRSelect
										id="employmentStatus"
										placeholder="Select your employment status"
										options={EMPLOYMENT_STATUS_OPTIONS}
										size={{
											base: "md",
											lg: "lg",
										}}
										chakraStyles={customStyles}
										value={EMPLOYMENT_STATUS_OPTIONS.find(
											(option) => option.value === field.value
										)}
										onChange={(selected) => {
											field.onChange(selected?.value);
										}}
									/>
								)}
							/>
							<FormErrorMessage>
								Please select your employment status
							</FormErrorMessage>
						</div>
					</div>
				</FormControl>

				{formData.employmentStatus &&
					formData.employmentStatus !== EmploymentStatus.Unemployed && (
						<FormControl
							isInvalid={
								!!(
									touchedFields.averageMonthlyIncome &&
									!formData.averageMonthlyIncome
								)
							}
						>
							<div className="flex flex-col gap-4">
								<label
									htmlFor="incomeLevel"
									className="text-base lg:text-xl text-[#212121] font-medium"
								>
									What is your average monthly income?
								</label>
								<div>
									<div>
										<InputGroup>
											<NumberInput
												className="w-full"
												onChange={(_, value) => {
													setValue("averageMonthlyIncome", value);
												}}
												value={formData.averageMonthlyIncome}
												id="averageMonthlyIncome"
												rounded={0}
												step={5000}
											>
												<NumberInputField
													rounded={0}
													border={"1px solid #D7D7D7"}
													py={{
														base: 5,
														lg: 8,
													}}
													placeholder="e.g. 100,000"
													px={{
														base: 3,
														lg: 5,
													}}
												/>
												<NumberInputStepper>
													<NumberIncrementStepper />
													<NumberDecrementStepper />
												</NumberInputStepper>
											</NumberInput>
										</InputGroup>
										<FormHelperText>
											I earn this monthly on average
										</FormHelperText>
									</div>
									<FormErrorMessage>
										Please enter your average monthly income
									</FormErrorMessage>
								</div>
							</div>
						</FormControl>
					)}

				<FormControl
					isInvalid={
						!!(touchedFields.incomeCurrency && !formData.incomeCurrency)
					}
				>
					<div className="flex flex-col gap-4">
						<label
							htmlFor="incomeCurrency"
							className="text-base lg:text-xl text-[#212121] font-medium"
						>
							What currency do you earn in?
						</label>
						<div className="flex flex-col gap-2">
							<Controller
								name="incomeCurrency"
								control={control}
								render={({ field }) => (
									<CRSelect
										value={field.value}
										id="currency"
										placeholder="Select option"
										options={Object.values(currencies).map((currency) => ({
											label: currency.name,
											value: currency.code,
										}))}
										size={{
											base: "md",
											lg: "lg",
										}}
										chakraStyles={customStyles}
										onChange={(e) => {
											setUserHasChangedCurrency(true);
											field.onChange(e);
										}}
									/>
								)}
							/>
							<FormErrorMessage>
								Please select the currency you earn in
							</FormErrorMessage>
						</div>
					</div>
				</FormControl>
				<FormControl
					isInvalid={
						!!(touchedFields.yearsOfExperience && !formData.yearsOfExperience)
					}
				>
					<div className="flex flex-col gap-4">
						<label
							htmlFor="yearsOfExperience"
							className="text-base lg:text-xl text-[#212121] font-medium"
						>
							How many years of experience do you have?
						</label>
						<div className="flex flex-col gap-1">
							<FormControl
								isInvalid={
									!formData.noExperience &&
									!!touchedFields.noExperience &&
									!formData.yearsOfExperience
								}
							>
								<div className="flex items-center gap-2">
									<Checkbox
										id="noExperience"
										colorScheme="brand"
										{...registerContactAndProfessionalInfo("noExperience")}
										isDisabled={!!formData.yearsOfExperience}
									/>
									<FormLabel
										fontSize={"14px"}
										color={"gray.500"}
										margin={0}
										fontWeight={"normal"}
										htmlFor="proDoesNotWishToDiscloseBudget"
									>
										No experience
									</FormLabel>
								</div>
							</FormControl>
							<Input
								{...registerContactAndProfessionalInfo("yearsOfExperience")}
								type="number"
								id="yearsOfExperience"
								placeholder="Enter your years of experience"
								isDisabled={formData.noExperience}
								rounded={0}
								py={{
									base: 5,
									lg: 8,
								}}
								px={{
									base: 3,
									lg: 5,
								}}
								border={"1px solid #D7D7D7"}
							/>
							<FormErrorMessage>
								Please enter your years of experience or select no experience
							</FormErrorMessage>
						</div>
					</div>
				</FormControl>
			</div>
		</div>
	);
}
